import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, voidRecord } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader, StatusBadge, Modal, Field, inputCls, PrimaryButton, EmptyState } from "@/components/lims";
import { canManageCatalogue, canDeleteCatalogue } from "@/lib/roles";
import { Plus, ListChecks, Trash, PencilSimple } from "@phosphor-icons/react";

const CATEGORIES = ["Chemistry", "Molecular", "Microbiology", "Immunoassay", "Hematology", "General"];
const empty = { name: "", method: "", category: "General", unit: "", spec_min: "", spec_max: "", spec_text: "", turnaround_days: "", price: "", active: true };

export default function Catalogue() {
  const { user } = useAuth();
  const canManage = canManageCatalogue(user);
  const canDelete = canDeleteCatalogue(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/catalogue");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ ...empty, ...a, spec_min: a.spec_min ?? "", spec_max: a.spec_max ?? "", turnaround_days: a.turnaround_days ?? "", price: a.price ?? "" });
    setOpen(true);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      spec_min: form.spec_min === "" ? null : parseFloat(form.spec_min),
      spec_max: form.spec_max === "" ? null : parseFloat(form.spec_max),
      turnaround_days: form.turnaround_days === "" ? null : parseInt(form.turnaround_days),
      price: form.price === "" ? null : parseFloat(form.price),
    };
    try {
      if (editing) { await api.put(`/catalogue/${editing.id}`, payload); toast.success("Assay updated"); }
      else { await api.post("/catalogue", payload); toast.success("Assay added"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!(await voidRecord(`/catalogue/${a.id}`, `assay ${a.name}`))) return;
    toast.success("Assay voided"); load();
  };

  const specLabel = (a) => {
    if (a.spec_min != null && a.spec_max != null) return `${a.spec_min} – ${a.spec_max} ${a.unit || ""}`;
    if (a.spec_min != null) return `≥ ${a.spec_min} ${a.unit || ""}`;
    if (a.spec_max != null) return `≤ ${a.spec_max} ${a.unit || ""}`;
    return a.spec_text || "—";
  };

  return (
    <Page>
      <PageHeader title="Test Catalog" subtitle="Assays, methods, and acceptance specifications"
        action={canManage && <PrimaryButton data-testid="new-assay-button" onClick={openNew}><Plus size={18} /> Add Assay</PrimaryButton>} />

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <EmptyState icon={ListChecks} title="No assays defined" subtitle="Define assays with specification limits for automatic OOS detection" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Code</th>
                <th className="px-5 py-3 font-semibold">Assay</th>
                <th className="px-5 py-3 font-semibold">Category</th>
                <th className="px-5 py-3 font-semibold">Method</th>
                <th className="px-5 py-3 font-semibold">Specification</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`assay-row-${a.assay_code}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3 font-mono text-xs text-slate-500">{a.assay_code}</td>
                  <td className="px-5 py-3 font-medium text-slate-900">{a.name}</td>
                  <td className="px-5 py-3 text-slate-600">{a.category}</td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{a.method || "—"}</td>
                  <td className="px-5 py-3 text-slate-600 font-mono text-xs">{specLabel(a)}</td>
                  <td className="px-5 py-3"><StatusBadge status={a.active ? "Active" : "Inactive"} /></td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {canManage && <button data-testid={`edit-assay-${a.assay_code}`} onClick={() => openEdit(a)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"><PencilSimple size={16} /></button>}
                      {canDelete && <button onClick={() => remove(a)} className="p-1.5 rounded-md text-slate-400 hover:text-status-critical hover:bg-status-critical/10 transition-colors"><Trash size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Assay" : "Add Assay"} testid="assay-modal">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Assay name"><input data-testid="assay-name-input" required value={form.name} onChange={set("name")} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category"><select value={form.category} onChange={set("category")} className={inputCls}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Method"><input value={form.method} onChange={set("method")} className={inputCls} placeholder="e.g. HPLC, PCR" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Unit"><input value={form.unit} onChange={set("unit")} className={inputCls} placeholder="mg/dL" /></Field>
            <Field label="Spec min"><input type="number" step="any" value={form.spec_min} onChange={set("spec_min")} className={inputCls} /></Field>
            <Field label="Spec max"><input type="number" step="any" value={form.spec_max} onChange={set("spec_max")} className={inputCls} /></Field>
          </div>
          <Field label="Qualitative spec (optional)"><input value={form.spec_text} onChange={set("spec_text")} className={inputCls} placeholder="e.g. Not Detected" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Turnaround (days)"><input type="number" value={form.turnaround_days} onChange={set("turnaround_days")} className={inputCls} /></Field>
            <Field label="Price"><input type="number" step="any" value={form.price} onChange={set("price")} className={inputCls} /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
            <PrimaryButton data-testid="save-assay-button" disabled={saving}>{editing ? "Save" : "Add"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </Page>
  );
}
